import { actorLabelRu } from '@wit-support/ui-domain';
import { POLICY_LABEL_RU, POLICY_SHORT_LABEL_RU } from './policy-text.js';
import { byNetwork, netText, networkName } from './network-text.js';
/**
 * Слова раздела «Соцсети»: подписи перечислений и фразы итога действия
 * (SPEC §1.6). Простым языком — их читает не разработчик, а человек, который
 * отвечает на комментарии.
 */
export const SOCIAL_ACTION_KIND_LABEL_RU = {
    like: 'Сердечко',
    reply: 'Ответ',
    hide: 'Скрыть',
    delete: 'Удалить',
};
export const SOCIAL_ACTION_STATUS_LABEL_RU = {
    proposed: 'Ждёт подтверждения',
    approved: 'В очереди',
    executing: 'Выполняется',
    done: 'Выполнено',
    failed: 'Ошибка',
    skipped: 'Пропущено',
    rejected: 'Отклонено',
};
export const SOCIAL_MODE_LABEL_RU = {
    off: 'Выключено',
    manual: 'Вручную',
    shadow: 'Тень',
    auto: 'Авто',
};
/** Пояснение режима — рядом с переключателем на «Настройках реагирования». */
export const SOCIAL_MODE_HINT_RU = {
    off: 'Выключено — только журнал',
    manual: 'Вручную — алгоритм предлагает, человек подтверждает',
    shadow: 'Тень (только показать решения) — алгоритм решает, но ничего не отправляет',
    auto: 'Авто — исполняется без подтверждения',
};
export const SOCIAL_AUTH_TYPE_LABEL_RU = {
    instagram_login: 'Instagram',
    facebook_login: 'Facebook',
};
/**
 * Подпись подключения в строке аккаунт × сеть (RV-FB-SPEC L11): в сетке рядом
 * стоят две строки с «Facebook», поэтому способ Facebook у Instagram —
 * «Instagram через Facebook», а у сети Facebook — «Facebook (Страница «…»)»
 * с именем Страницы от движка. Без сети — прежняя подпись способа.
 */
export function authTypeLabel(authType, network, externalName) {
    if (authType === 'facebook_login' && network === 'facebook') {
        return externalName ? `Facebook (Страница «${externalName}»)` : 'Facebook (Страница)';
    }
    if (authType === 'facebook_login' && network === 'instagram')
        return 'Instagram через Facebook';
    return SOCIAL_AUTH_TYPE_LABEL_RU[authType];
}
/**
 * Порядок способов входа на экране «Подключения» (бейджи, меню строки):
 * Facebook — основной, поэтому первым (P3-E).
 */
export const SOCIAL_AUTH_TYPE_ORDER = [
    'facebook_login',
    'instagram_login',
];
/**
 * Какой это способ по замыслу — в меню строки и в заголовке диалога:
 * «Подключить токен Facebook — основной». Не путать с ролью подключения от
 * движка (`role`): без Facebook основным становится Instagram.
 */
export const SOCIAL_AUTH_TYPE_KIND_RU = {
    facebook_login: 'основной',
    instagram_login: 'запасной',
};
/** Бейдж роли подключения (`role` движка): через какое подключение сейчас идёт работа. */
export const SOCIAL_CONNECTION_ROLE_LABEL_RU = {
    primary: 'основное',
    fallback: 'запасное',
};
/** Запасного пригодного подключения нет (`fallbackAuthType: null`). */
export const SOCIAL_NO_FALLBACK_TEXT_RU = 'Запасного подключения нет — комментарии перестанут обрабатываться';
/**
 * Подсказка к бейджу роли. «Включится само» / «работу возьмёт запасное» —
 * только если движок назвал запасное (`fallbackAuthType`, RV-P3 M1).
 */
export function connectionRoleHint(role, fallbackAuthType) {
    if (role === 'primary') {
        const tail = fallbackAuthType
            ? `Если оно перестанет действовать, работу само возьмёт запасное подключение ${SOCIAL_AUTH_TYPE_LABEL_RU[fallbackAuthType]}.`
            : `${SOCIAL_NO_FALLBACK_TEXT_RU}, если это подключение перестанет действовать.`;
        return `Через это подключение движок читает комментарии и выполняет все действия. ${tail}`;
    }
    const tail = fallbackAuthType
        ? ' Включится само, если основное отключат или его токен перестанет действовать.'
        : '';
    return `Подключение работает, но движок им не пользуется, пока работает основное.${tail}`;
}
/** Что даёт каждый способ входа — подзаголовок диалога «Подключить токен». */
export const SOCIAL_AUTH_TYPE_PURPOSE_RU = {
    facebook_login: 'Основной способ: через него движок читает комментарии, отвечает, скрывает, удаляет и ставит сердечки.',
    instagram_login: 'Запасной способ: работает, только если токен Facebook не подключён или перестал действовать. Читает комментарии, отвечает, скрывает и удаляет, но сердечки не ставит.',
};
/**
 * Права токена Facebook (TOKENS-GUIDE §4e, токен системного пользователя
 * «WIT Social»). Обязательное для подключения — `instagram_manage_comments`
 * (P3-E); без `instagram_manage_engagement` не будет только сердечек.
 */
export const SOCIAL_FB_TOKEN_SCOPES = [
    'instagram_basic',
    'instagram_manage_comments',
    'instagram_manage_engagement',
    'pages_show_list',
    'pages_read_engagement',
    'pages_read_user_content',
];
/**
 * Права токена системного пользователя для Страницы Facebook (FB-SPEC §3.3):
 * те же шесть, что для Instagram, и `pages_manage_engagement` — без него
 * Страница только читается (ответ, сердечко, скрытие, удаление не пойдут).
 */
export const SOCIAL_FB_PAGE_TOKEN_SCOPES = [
    ...SOCIAL_FB_TOKEN_SCOPES,
    'pages_manage_engagement',
];
/** Подсказка токена для сети Facebook (FB-SPEC §6.5). */
export const SOCIAL_FB_PAGE_TOKEN_HINT_RU = `Тот же токен системного пользователя «WIT Social», что и для Instagram. Права: ${SOCIAL_FB_PAGE_TOKEN_SCOPES.join(', ')}. Обязательное — pages_read_user_content: без него комментарии Страницы не читаются. Без pages_manage_engagement — только чтение: ответы, сердечки, скрытие и удаление не пойдут. У системного пользователя на Странице нужны доступы «Контент» и «Активность сообщества» (Business Settings → Системные пользователи → Назначить ресурсы).`;
/** Что даёт подключение Страницы — подзаголовок диалога «Подключить токен» у сети Facebook. */
export const SOCIAL_FB_PAGE_PURPOSE_RU = 'Страница Facebook этого аккаунта: движок читает комментарии под её роликами, а с правом pages_manage_engagement — отвечает, ставит сердечки, скрывает и удаляет. Страницу движок находит сам — по Instagram этого аккаунта.';
/** Подсказка токена по способу и сети: у сети Facebook — свои права (7 штук). */
export function tokenHint(authType, network) {
    if (network === 'facebook')
        return SOCIAL_FB_PAGE_TOKEN_HINT_RU;
    return SOCIAL_TOKEN_HINT_RU[authType];
}
/** Подзаголовок диалога «Подключить токен» по способу и сети. */
export function authTypePurpose(authType, network) {
    if (network === 'facebook')
        return SOCIAL_FB_PAGE_PURPOSE_RU;
    return SOCIAL_AUTH_TYPE_PURPOSE_RU[authType];
}
/** Подсказка, где взять токен (SPEC §1.8). */
export const SOCIAL_TOKEN_HINT_RU = {
    facebook_login: `Нужен токен системного пользователя приложения «WIT Social» с правами: ${SOCIAL_FB_TOKEN_SCOPES.join(', ')}. Обязательное — instagram_manage_comments: без него комментарии не читаются. Без instagram_manage_engagement не будет только сердечек. Один и тот же токен можно вставить во все аккаунты — движок сам найдёт Страницу нужного Instagram.`,
    instagram_login: 'Нужен, только если токена Facebook нет. Токен берётся в панели разработчика Meta → Instagram → API setup with Instagram business login → Generate token. Действует 60 дней, мы продлеваем сами.',
};
/**
 * Какие токены Facebook подходят — список в диалоге «Подключить токен
 * Facebook — основной». Первым — рекомендуемый.
 */
export const SOCIAL_FB_TOKEN_KINDS_RU = [
    {
        title: 'Токен системного пользователя приложения «WIT Social» — рекомендуется',
        text: 'Не истекает. Страница Facebook и Instagram этого аккаунта должны быть назначены системному пользователю.',
    },
    {
        title: 'Токен пользователя Facebook',
        text: 'Живёт 60 дней и сам не продлевается: за неделю до срока строка станет жёлтой — тогда подключите новый.',
    },
    {
        title: 'Токен Страницы Facebook',
        text: 'Страницы, к которой привязан этот Instagram. Подключится, но работают ли с ним комментарии и сердечки, пока не проверено — лучше один из первых двух.',
    },
];
/** Подпись бейджа, когда сеть подтвердила бессрочный токен (движок отдал `tokenExpiresAt: null`, `expiresAssumed: false`). */
export const SOCIAL_NEVER_EXPIRES_TEXT_RU = 'бессрочно';
/**
 * Пояснение к «(предположительно)» у срока токена. Instagram Login движок
 * продлевает сам — точный срок придёт с первым продлением. Facebook Login
 * не продлевается вовсе: срок так и останется предполагаемым, токен меняют
 * вручную.
 */
export const SOCIAL_ASSUMED_EXPIRY_TEXT_RU = {
    instagram_login: 'Срок предполагаемый: точный срок станет известен после первого продления',
    facebook_login: 'Срок предполагаемый: токен Facebook не продлевается автоматически — до этой даты подключите новый токен заново',
};
/**
 * Ошибки подключения и опроса (`lastError`, `lastPollError`). Движок пишет
 * туда код и, для ошибок сети, подкоды Graph: `token_invalid 190/460 <fbtrace>`.
 * Человеку — фраза по первому слову (коду); подкоды — только во всплывающей
 * подсказке для разработчика.
 */
const CONNECTION_ERROR_TEXT_RU = {
    token_invalid: 'Токен недействителен или отозван — подключите заново',
    token_expired: 'Срок токена истёк — подключите заново',
    account_mismatch: 'Токен выдан другому аккаунту — подключите токен нужного профиля',
    missing_scope: 'У токена нет права instagram_manage_comments — комментарии не читаются и действия не выполняются, выдайте токен с этим правом',
    no_instagram: 'Токен не видит Страницу Facebook с привязанным Instagram — назначьте Страницу токену или подключите другой токен',
    permission: 'У токена не хватает прав — подключите токен с нужными правами',
    secrets_key_mismatch: 'Движок не может расшифровать токен (сменился ключ шифрования) — подключите заново',
    secrets_not_configured: 'В движке не задан ключ шифрования токенов',
    rate_limited: 'Сеть попросила подождать — опрос продолжится сам',
    blocked: 'Сеть временно заблокировала запросы — опрос продолжится сам',
    not_found: 'Сеть не нашла аккаунт или пост',
    network: 'Сеть не ответила — движок попробует ещё раз',
    unknown: 'Сеть вернула непонятную ошибку — движок попробует ещё раз',
    internal_error: 'Внутренняя ошибка движка — движок попробует ещё раз',
    // Facebook (FB-SPEC §2.4, §3.2)
    no_page_token: 'Сеть не отдала токен Страницы — ответы, сердечки, скрытие и удаление не пойдут. Проверьте, что Страница назначена системному пользователю, и нажмите «Проверить»',
    missing_scope_actions: 'Нужно право pages_manage_engagement — перевыпустите токен с этим правом. Пока только чтение комментариев: ответы, сердечки, скрытие и удаление не пойдут',
};
/** У сети Facebook часть кодов значит другое (Страница, а не Instagram-аккаунт). */
const FB_CONNECTION_ERROR_TEXT_RU = {
    missing_scope: 'У токена нет права pages_read_user_content — комментарии Страницы не читаются, выдайте токен с этим правом',
    account_mismatch: 'Токен не видит Страницу Facebook этого аккаунта — назначьте Страницу системному пользователю (Business Settings → Системные пользователи → Назначить ресурсы)',
    permission: 'Токену не хватает прав или доступа к Странице — проверьте права токена и доступы системного пользователя на Странице, потом нажмите «Проверить»',
    rate_limited: 'Facebook попросил подождать (лимит Страницы) — опрос продолжится сам',
};
/** Код из строки ошибки подключения: первое слово (`token_invalid 190/460` → `token_invalid`). */
export function connectionErrorCode(raw) {
    return raw.trim().split(/\s+/)[0] ?? '';
}
/**
 * `lastError: permission` у подключения Facebook (RV-P3 M1): сеть отказала
 * опросу по правам, движок перестал брать это подключение основным.
 */
export const SOCIAL_FB_PERMISSION_TEXT_RU = 'Facebook-токену не хватает прав на комментарии — работа идёт через запасное подключение, если оно есть; нажмите «Проверить» после выдачи прав';
/**
 * Ошибка подключения/опроса словами; незнакомый код — запасная фраза без
 * подкодов. `authType` — у ошибки самого подключения (не опроса): у Facebook
 * `permission` значит «нет прав на комментарии». `network` — у сети Facebook
 * (Страница) свои фразы для прав и «не тот аккаунт».
 */
export function describeConnectionError(raw, authType, network) {
    const code = connectionErrorCode(raw);
    if (network === 'facebook') {
        const fb = FB_CONNECTION_ERROR_TEXT_RU[code];
        if (fb)
            return fb;
    }
    else if (authType === 'facebook_login' && code === 'permission') {
        return SOCIAL_FB_PERMISSION_TEXT_RU;
    }
    return CONNECTION_ERROR_TEXT_RU[code] ?? `Ошибка сети (код: ${code || raw})`;
}
/**
 * Имя политики → человеческое имя для списка «Алгоритм»: известные —
 * своими словами («Алгоритм v1 (рекомендуется)», «Нет — только вручную»);
 * незнакомая — имя и описание движка.
 */
export function policyLabel(name, description) {
    const known = POLICY_LABEL_RU[name];
    if (known)
        return known;
    return description ? `${name} — ${description}` : name;
}
/**
 * Кто: `policy:<имя>` — алгоритм; `admin:<uuid> <email>` (так admin-api
 * передаёт автора в движок) — e-mail человека, без префикса и uuid
 * (общий `actorLabelRu` кита); прочее — как есть.
 */
export function actionAuthorLabel(author) {
    if (author.startsWith('policy:')) {
        const name = author.slice(7);
        return POLICY_SHORT_LABEL_RU[name] && name !== 'noop'
            ? POLICY_SHORT_LABEL_RU[name]
            : `Алгоритм «${policyLabel(name)}»`;
    }
    return actorLabelRu(author) ?? author;
}
/**
 * Фразы для `skipReason` и `error.code` (SPEC §1.6). Поле в DTO — строка:
 * новая причина движка не должна ломать экран, для неё есть запасная фраза.
 */
const OUTCOME_TEXT_RU = {
    comment_hidden: 'Нельзя ответить на скрытый комментарий',
    not_allowed_by_network: 'Сеть не разрешила действие (например, удалить чужой комментарий)',
    private_account: 'Автор — приватный аккаунт, сердечко недоступно',
    comment_gone: 'Комментарий уже удалён',
    own_comment: 'Это наш комментарий',
    unsupported: 'Это действие недоступно при текущем подключении',
    connection_missing: 'Нет подключения для этого действия',
    auto_delete_off: 'Автоудаление выключено в настройках',
    unknown_outcome: 'Ответ отправлен, но подтверждение не пришло — проверьте в Instagram',
    interrupted: 'Прервано перезапуском, повторено',
    token_invalid: 'Токен недействителен — обновите подключение',
    rate_limited: 'Instagram попросил подождать',
    // FX-SE H1 / RULINGS-SAFETY-3 H1: действие, на котором пришёл 368, не
    // повторяется ни автоматикой, ни кнопкой «Повторить»; создавать заново не
    // предлагаем — повтор во время ограничения продлевает его
    blocked: 'Instagram временно ограничил действия аккаунта (ошибка 368) — действие вызвало ограничение и не будет повторено',
    // Защита от блокировки (RULINGS-SAFETY «Движок» п.5, п.7): движок проверяет
    // перед отправкой и пропускает действие с этой причиной.
    spam_rejected: 'Instagram отклонил ответ как спам — этот текст 7 дней не используем',
    comment_has_link: 'В комментарии ссылка (похоже на спам) — не отвечаем и не ставим сердечко',
    reply_emoji_only: 'Ответ только из эмодзи — Instagram считает такие ответы спамом',
    reply_too_short: 'Ответ короче 3 слов — Instagram считает такие ответы спамом',
    reply_has_link: 'В ответе ссылка — такие ответы не отправляем',
    reply_has_mention: 'В ответе @-упоминание — такие ответы не отправляем',
    reply_author_post: 'Этому автору под этим постом уже ответили',
    reply_author_day: 'Этому автору уже ответили за последние 24 часа — алгоритм отвечает одному автору не чаще 1 раза за 24 часа',
    reply_author_week: 'Этому автору уже отвечали на этой неделе — алгоритм не отвечает ему повторно',
    like_author_day: 'Этому автору на этом аккаунте уже поставили 2 сердечка за 24 часа — лимит',
    like_author_all: 'Этому автору с наших аккаунтов уже поставили 3 сердечка за 24 часа — лимит по всем аккаунтам',
    reply_text_repeated: 'Такой же текст уже отправляли за последние сутки — напишите по-другому',
    reply_text_burned: 'Этот текст Instagram отклонил как спам — 7 дней его не используем',
    // FX-SE M2: авто-действие по комментарию старше 48 ч при исполнении пропускается
    comment_stale: 'Комментарий старше 48 часов — автоматическое действие не отправляем (вручную можно)',
    // engage-v1 (AN-1 §6.3): автор пишет много обрывков — алгоритм его пропускает
    noise_author: 'Автор пишет много коротких обрывков — алгоритм на такие комментарии не реагирует',
    // E-POL: все подходящие заготовки ответа уже использованы
    no_template: 'Все подходящие тексты ответа уже использованы (за сутки, под этим постом или отклонены как спам) — ответ не отправляем',
    // E-POL: предложение «только с человеком» одобрила автоматика — исполнитель не выполняет
    needs_approval: 'Это действие выполняется только после подтверждения человеком',
};
/** Своя фраза к известному коду пропуска/ошибки; незнакомый — null. `network` — имя сети в фразе. */
export function knownOutcomeText(code, network) {
    const known = OUTCOME_TEXT_RU[code];
    return known !== undefined ? netText(known, network) : null;
}
/** Движок может прислать вместо кода готовую русскую фразу — её показываем как есть. */
const isPhraseRu = (s) => /[а-яё]/i.test(s);
/**
 * Фраза причины/ошибки по коду; незнакомый код — запасная фраза с кодом.
 * `network` — сеть действия: «Facebook отклонил ответ как спам…» (у
 * Instagram — прежние фразы).
 */
export function describeSocialOutcomeCode(code, kind, network) {
    const known = OUTCOME_TEXT_RU[code];
    if (known)
        return netText(known, network);
    if (isPhraseRu(code))
        return code;
    return kind === 'skip' ? `Пропущено (причина: ${code})` : `Ошибка сети (код: ${code})`;
}
/**
 * Итог действия одной фразой для журнала карточки комментария.
 * Причина пропуска и ошибка важнее статуса: «Пропущено» без причины ничего
 * не объясняет.
 */
export function describeSocialOutcome(a, network) {
    // Готовые фразы движка (SAFE-E) — главный источник; свои — запасные.
    if (a.skipReason)
        return a.skipText ?? describeSocialOutcomeCode(a.skipReason, 'skip', network);
    if (a.error && a.errorText)
        return a.errorText;
    if (a.status === 'approved' && (a.waitText || a.waitReason)) {
        return `В очереди, ждёт: ${a.waitText ?? describeWaitReason(a.waitReason, network)}`;
    }
    if (a.error) {
        const base = describeSocialOutcomeCode(a.error.code, 'error', network);
        return a.error.graphCode !== null && !(a.error.code in OUTCOME_TEXT_RU)
            ? `${base}, сеть ответила кодом ${a.error.graphCode}`
            : base;
    }
    switch (a.status) {
        case 'done':
            return a.dryRun ? 'Сухой режим: отмечено выполненным, в сеть не отправлялось' : 'Выполнено';
        case 'rejected':
            // RV-SHADOW M1: движок снял действие алгоритма при смене режима на «Тень»/«Выключено»
            if (a.rejectReason === MODE_CHANGED_REJECT_REASON)
                return MODE_CHANGED_TEXT_RU;
            return a.rejectReason ? `Отклонено: ${a.rejectReason}` : 'Отклонено';
        case 'proposed':
            return 'Алгоритм предложил — ждёт подтверждения';
        case 'approved':
            return a.nextAttemptAt ? 'В очереди, повтор позже' : 'В очереди';
        case 'executing':
            return 'Выполняется';
        case 'failed':
            return 'Не выполнено';
        case 'skipped':
            return 'Пропущено';
    }
}
/** `rejectReason` движка: действие алгоритма снято при смене режима (RV-SHADOW M1). */
export const MODE_CHANGED_REJECT_REASON = 'mode_changed';
export const MODE_CHANGED_TEXT_RU = 'Снято при смене режима';
/** Тост после правки настроек: сколько действий алгоритма снято; 0/null — ничего. */
export function settingsSavedText(rejected) {
    return rejected !== null && rejected > 0
        ? `Режим изменён. Снято действий алгоритма: ${rejected}`
        : 'Настройки сохранены';
}
/** Ответ «отправлен, но не подтверждён»: перед повтором — проверить в Instagram. */
export function isUnknownOutcome(a) {
    return a.error?.code === 'unknown_outcome';
}
/**
 * У каких действий в журнале есть «Повторить». Действие, на котором Instagram
 * ответил ограничением 368 (`error.code: 'blocked'`), — никогда
 * (RULINGS-SAFETY-3 H1, регламент §6 «не пробовать ещё раз после
 * блокировки»): повтор ушёл бы первым после паузы, при живом ограничении.
 */
export function canRetry(a) {
    if (a.error?.code === 'blocked')
        return false;
    return a.status === 'failed' || a.status === 'skipped';
}
// ---------------------------------------------------------------------------
// Защита аккаунтов от блокировки (RULINGS-SAFETY)
// ---------------------------------------------------------------------------
/** Почему действия аккаунта на паузе (`safety.pauseReason`) — коды движка. */
const PAUSE_REASON_TEXT_RU = {
    blocked_368: 'Instagram временно ограничил действия аккаунта (ошибка 368) — все действия остановлены на 48 часов',
    blocked_repeat: 'Instagram повторно ограничил аккаунт за 30 дней — автоматика выключена до кнопки «Возобновить»',
    rate_limited: 'Instagram попросил снизить частоту запросов — действия на паузе 1 час',
    consecutive_failures: 'Три отказа сети подряд — действия на паузе до начала следующего часа (не меньше 15 минут)',
    spam_replies: 'Instagram отклонил 3 ответа как спам за час — ответы на паузе 12 часов',
    graph_load_high: 'Высокая нагрузка на API Instagram — действия на паузе, пока она не спадёт',
    graph_load_poll: 'Высокая нагрузка на API Instagram — сбор комментариев на паузе, пока она не спадёт',
    manual_stop: 'Действия аккаунта остановлены вручную',
    // Коды из перечня ответа «Возобновить» (FX-SE M3) — запасные фразы, если движок не прислал свою
    blocked: 'Instagram ограничил аккаунт — автоматика была выключена',
    paused: 'Действия аккаунта на паузе',
    global_stop: 'Включён «Стоп всех действий»',
    mode_off: 'Режим аккаунта «Выключено» — действия не отправляются',
};
/**
 * Паузы «из-за другой сети того же аккаунта» (FB-SPEC §4.2, M3): у Instagram
 * и Страницы Facebook одного аккаунта один бизнес и один токен, поэтому
 * ограничение одной сети приостанавливает действия другой. `net` — сеть этой
 * пары, `sibling` — сеть-источник (null — не знаем, какая).
 */
export function siblingPauseText(reason, network, sibling) {
    const net = networkName(network);
    const src = sibling ? networkName(sibling) : null;
    if (reason === 'blocked_sibling') {
        return src
            ? `Приостановлено, потому что ${src} этого аккаунта ограничен (ошибка 368) — действия на ${net} стоят 48 часов, комментарии собираются`
            : `Приостановлено из-за ограничения другой сети этого аккаунта (ошибка 368) — действия на ${net} стоят 48 часов, комментарии собираются`;
    }
    return src
        ? `Ответы на паузе 12 часов, потому что ${src} этого аккаунта отклонил 3 ответа как спам`
        : 'Ответы на паузе 12 часов из-за спам-отказов на другой сети этого аккаунта';
}
const SIBLING_REASONS = new Set(['blocked_sibling', 'spam_sibling']);
/** Пауза — словами: известный код — фраза, русская фраза движка — как есть. */
/**
 * Действует пауза после ограничения Instagram (368): `pauseReason` —
 * `blocked_368` (первая, 48 ч) или `blocked_repeat` (повторная). Движок
 * отдаёт `pauseReason` только пока пауза действует, поэтому срок не
 * проверяем. Статус НЕ смотрим (FX-SE3): при «ручной стоп + пауза 368» он
 * `manual_stop`, при общем стопе — `global_stop`, при «Выключено» —
 * `mode_off`, а пауза всё равно действует. В это время движок не принимает
 * ручных действий (409 `social_account_blocked`, RULINGS-SAFETY-3 H1), а
 * «Возобновить» снимает паузу только с подтверждением «ограничение снято» (M2).
 */
export function blockPauseActive(s) {
    return s?.pauseReason === 'blocked_368' || s?.pauseReason === 'blocked_repeat';
}
export function describePauseReason(reason, network, sibling = null) {
    if (reason === null)
        return 'Действия аккаунта на паузе';
    if (SIBLING_REASONS.has(reason)) {
        return siblingPauseText(reason, network, sibling);
    }
    const known = PAUSE_REASON_TEXT_RU[reason];
    if (known !== undefined)
        return netText(known, network);
    return isPhraseRu(reason) ? reason : 'Действия аккаунта на паузе';
}
/** Подключение, к которому относится пауза из перечня «Возобновить» (`authType` движка). */
const PAUSE_AUTH_TYPE_RU = {
    instagram_login: 'подключение Instagram',
    facebook_login: 'подключение Facebook',
};
/**
 * Пауза из ответа «Возобновить» словами: фраза движка, иначе своя по коду;
 * у паузы одного подключения — какого; срок (уже в часовом поясе админки) —
 * в конце.
 */
export function pauseInfoText(p, until = null, network) {
    const text = p.text ?? describePauseReason(p.reason, network);
    const conn = p.authType ? PAUSE_AUTH_TYPE_RU[p.authType] : undefined;
    const withConn = conn ? `${text} (${conn})` : text;
    return until ? `${withConn} — до ${until}` : withConn;
}
/**
 * Итог «Возобновить» (RULINGS-SAFETY-2): что снято, что осталось. Движок без
 * перечня (`cleared`/`remaining` null) — только заголовок. `fmt` переводит
 * срок оставшейся паузы в строку часового пояса админки. У снятых срок не
 * пишем: у снятой блокировки движок кладёт туда момент блокировки, а не конец.
 */
export function resumeSummary(account, r, fmt, network) {
    const parts = [];
    // RULINGS-SAFETY-3 M1: после повторной блокировки движок возвращает режим
    // «Вручную» (не «Авто», даже если он был) — человек должен это увидеть
    // (FX-SE3: `off` — режим был «Выключено» до блокировки и таким остался)
    if (r.modeRestored === 'manual') {
        const was = r.modeBeforeBlock === 'auto' ? ` (до блокировки был «${SOCIAL_MODE_LABEL_RU.auto}»)` : '';
        parts.push(`Режим возвращён: ${SOCIAL_MODE_LABEL_RU.manual}${was}. «Авто» владелец включает отдельно в «Настройках реагирования».`);
    }
    else if (r.modeRestored === 'off') {
        parts.push(`Режим остался: ${SOCIAL_MODE_LABEL_RU.off} (как до блокировки) — действия не пойдут, пока режим не сменят в «Настройках реагирования».`);
    }
    else if (r.modeRestored === 'shadow') {
        // SHADOW-E: из «Тени» движок возвращает «Тень» — она ничего не отправляет
        parts.push(`Режим остался: ${SOCIAL_MODE_LABEL_RU.shadow} (как до блокировки) — алгоритм только показывает решения, в сеть ничего не уходит.`);
    }
    else if (r.modeRestored != null) {
        parts.push(`Режим возвращён: ${SOCIAL_MODE_LABEL_RU[r.modeRestored]}.`);
    }
    if (r.cleared !== null) {
        parts.push(r.cleared.length > 0
            ? `Снято: ${r.cleared.map((p) => pauseInfoText(p, null, network)).join('; ')}.`
            : 'Снимать было нечего.');
    }
    // Итог движка важнее перечня: `status` после «Возобновить» не `active` —
    // действия ещё ждут, даже если перечень пуст или его нет (FX-SE M3).
    const notActive = r.status != null && r.status !== 'active';
    const stillPaused = (r.remaining !== null && r.remaining.length > 0) || notActive;
    if (r.remaining !== null && r.remaining.length > 0) {
        const rest = r.remaining.map((p) => pauseInfoText(p, p.until ? fmt(p.until) : null, network));
        parts.push(`Осталось: ${rest.join('; ')}. Эти паузы кнопка не снимает — они закончатся сами или снимаются отдельно.`);
    }
    if (notActive && (r.remaining === null || r.remaining.length === 0) && r.statusText) {
        parts.push(`Сейчас: ${r.statusText}.`);
    }
    return {
        title: stillPaused
            ? `${account}: возобновлено, но часть пауз ещё действует`
            : `${account}: действия возобновлены`,
        description: parts.length > 0 ? parts.join(' ') : null,
    };
}
/** Плашка у аккаунта с `blockedAt` (текст RULINGS-SAFETY). */
export const SOCIAL_BLOCKED_TEXT_RU = 'Instagram ограничил аккаунт — автоматика выключена';
export const SOCIAL_BLOCKED_HINT_RU = 'Действия не отправляются. Откройте Instagram на телефоне, убедитесь, что ограничение снято, и только потом нажмите «Возобновить».';
/**
 * Подтверждение «Возобновить», пока действует пауза после ограничения
 * Instagram (368) — строгое (RULINGS-SAFETY-3 M2): кнопка снимет и эту паузу,
 * поэтому сначала — «Статус аккаунта» в приложении Instagram.
 */
export const SOCIAL_BLOCK_PAUSE_RESUME_CONFIRM_RU = 'Аккаунт на паузе после ограничения Instagram (ошибка 368). «Возобновить» снимет и эту паузу. Убедитесь в приложении Instagram → «Настройки» → «Статус аккаунта», что ограничение снято. Если возобновить раньше, ограничение продлится и аккаунт могут заблокировать надолго.';
/** Подтверждение «Возобновить» после блокировки. */
export const SOCIAL_RESUME_CONFIRM_RU = 'Убедитесь в приложении Instagram → «Настройки» → «Статус аккаунта», что ограничение снято. Если возобновить раньше, аккаунт могут заблокировать надолго. Режим аккаунта вернётся в «Вручную» (из «Тени» — останется «Тень») — «Авто» включите отдельно, когда убедитесь, что всё в порядке.';
/**
 * Те же фразы для Страницы Facebook: где проверять ограничение — на Странице
 * в Facebook или в Meta Business Suite (приложения «Статус аккаунта» у
 * Страницы нет), а Страница — рекламный актив.
 */
export const SOCIAL_FB_BLOCKED_TEXT_RU = 'Facebook ограничил Страницу — автоматика выключена';
export const SOCIAL_FB_BLOCKED_HINT_RU = 'Действия не отправляются. Откройте Страницу в Facebook или в Meta Business Suite, убедитесь, что ограничение снято, и только потом нажмите «Возобновить».';
export const SOCIAL_FB_BLOCK_PAUSE_RESUME_CONFIRM_RU = 'Страница на паузе после ограничения Facebook (ошибка 368). «Возобновить» снимет и эту паузу. Убедитесь на Странице в Facebook или в Meta Business Suite, что ограничение снято. Если возобновить раньше, ограничение продлится — а Страница рекламный актив, ограничение может задеть и рекламу.';
export const SOCIAL_FB_RESUME_CONFIRM_RU = 'Убедитесь на Странице в Facebook или в Meta Business Suite, что ограничение снято. Если возобновить раньше, Страницу могут ограничить надолго — это может задеть и рекламу. Режим вернётся в «Вручную» (из «Тени» — останется «Тень») — «Авто» включите отдельно, когда убедитесь, что всё в порядке.';
/** Плашка блокировки по сети. */
export const blockedText = (network) => byNetwork(network, SOCIAL_BLOCKED_TEXT_RU, SOCIAL_FB_BLOCKED_TEXT_RU);
export const blockedHint = (network) => byNetwork(network, SOCIAL_BLOCKED_HINT_RU, SOCIAL_FB_BLOCKED_HINT_RU);
/** Строгое подтверждение «Возобновить» при паузе 368 — по сети. */
export const blockPauseResumeConfirm = (network) => byNetwork(network, SOCIAL_BLOCK_PAUSE_RESUME_CONFIRM_RU, SOCIAL_FB_BLOCK_PAUSE_RESUME_CONFIRM_RU);
/** Подтверждение «Возобновить» после повторной блокировки — по сети. */
export const resumeConfirm = (network) => byNetwork(network, SOCIAL_RESUME_CONFIRM_RU, SOCIAL_FB_RESUME_CONFIRM_RU);
export const SOCIAL_GLOBAL_STOP_CONFIRM_RU = 'Ни один аккаунт не будет ставить сердечки, отвечать, скрывать и удалять комментарии, пока владелец не нажмёт «Возобновить все действия». Действия в очереди подождут. Комментарии продолжат собираться.';
export const SOCIAL_GLOBAL_RESUME_CONFIRM_RU = 'Действия из очереди снова начнут уходить в соцсети — с обычными лимитами и паузами.';
/** Вид лимита — заголовком («Ответы: 15 в час»). */
export const SOCIAL_LIMIT_KIND_LABEL_RU = {
    reply: 'Ответы',
    like: 'Сердечки',
    hide: 'Скрытие',
    delete: 'Удаление',
    total: 'Все действия вместе',
};
/** Вид лимита в родительном падеже — для «ответов 3 из 15 в час». */
const LIMIT_KIND_GENITIVE_RU = {
    reply: 'ответов',
    like: 'сердечек',
    hide: 'скрытий',
    delete: 'удалений',
    total: 'всех действий',
};
/** Порядок показа: ответы — главный риск. */
export const SOCIAL_LIMIT_KIND_ORDER = [
    'reply',
    'like',
    'hide',
    'delete',
    'total',
];
/**
 * «Использовано ответов 3 из 15 в час · 7 из 60 за сутки». Лимита нет —
 * только число.
 */
export function usageText(kind, u) {
    const hour = u.perHour !== null ? `${u.usedHour} из ${u.perHour} в час` : `${u.usedHour} за час`;
    const day = u.per24h !== null ? ` · ${u.used24h} из ${u.per24h} за сутки` : '';
    return `${LIMIT_KIND_GENITIVE_RU[kind]} ${hour}${day}`;
}
/**
 * Общий суточный лимит всех действий (`safety.dailyTotal`, FX-SE L5):
 * «за сутки (общий): 40 из 150». Сутки — UTC, лимит действует поверх
 * лимитов по виду.
 */
export function dailyTotalText(d) {
    return `за сутки (общий): ${d.usedToday} из ${d.perDay}`;
}
/** Общий суточный лимит исчерпан — действия ждут 00:00 UTC. */
export function dailyTotalExhausted(d) {
    return d.usedToday >= d.perDay;
}
/** Пояснение к общему суточному лимиту (подсказка): фраза движка или своя. */
export const SOCIAL_DAILY_TOTAL_HINT_RU = 'Общий суточный лимит всех действий аккаунта (сутки UTC) — действует поверх лимитов по видам';
/** Лимит исчерпан (в час или за сутки) — строка подсвечивается. */
export function usageExhausted(u) {
    return ((u.perHour !== null && u.usedHour >= u.perHour) || (u.per24h !== null && u.used24h >= u.per24h));
}
/** Множитель словами: 0.33 → «треть», 0.66 → «две трети». */
function factorText(f) {
    if (Math.abs(f - 0.33) < 0.02)
        return 'треть';
    if (Math.abs(f - 0.66) < 0.02)
        return 'две трети';
    return `${Math.round(f * 100)} %`;
}
/**
 * Прогрев: «Прогрев нового аккаунта: 4-й день из 14. Сейчас лимиты — треть от
 * обычных, с 8-го дня — две трети, с 15-го дня — полные.» Прогрев закончился —
 * null (показывать нечего).
 */
export function warmupText(w) {
    if (w.factor >= 1)
        return null;
    const next = w.day < 7 ? ', с 8-го дня — две трети' : '';
    return `Прогрев нового аккаунта: ${w.day + 1}-й день из 14. Сейчас лимиты — ${factorText(w.factor)} от обычных${next}, с 15-го дня — полные.`;
}
/** Почему подтверждённое действие ждёт (`waitReason`) — запасные фразы, если движок не прислал `waitText`. */
const WAIT_REASON_TEXT_RU = {
    global_stop: 'включён «Стоп всех действий»',
    mode_off: 'режим аккаунта «Выключено» — очередь не исполняется',
    manual_stop: 'действия аккаунта остановлены вручную',
    blocked: 'Instagram ограничил аккаунт — нужно «Возобновить»',
    paused: 'действия аккаунта на паузе',
    replies_paused: 'ответы аккаунта на паузе после отказов антиспама',
    interval: 'выдерживаем паузу между действиями',
    limit_hour: 'достигнут лимит этого вида действий за час',
    limit_24h: 'достигнут лимит этого вида действий за сутки',
    limit_total: 'достигнут общий лимит действий за час',
    reply_burst: 'не больше 3 ответов за 5 минут',
    daily_limit: 'достигнут общий суточный лимит всех действий (сутки UTC, поверх лимитов по виду)',
    // engage-v1 (E-POL): окно автоответов, потолок поста, разнос лайка и ответа
    reply_window: 'автоответы уходят с 11:00 до 03:00 UTC — ждём начала окна',
    post_limit: 'под этим постом за час уже много наших действий (не больше 20 сердечек и 6 ответов)',
    comment_gap: 'сердечко и ответ на один комментарий разносим хотя бы на минуту',
    // SHADOW-E: авто-действие, поставленное ещё в «Авто», в «Тени» не исполняется
    mode_shadow: 'режим аккаунта «Тень» — автоматические действия не исполняются',
};
/** У Facebook числа потолка поста свои (FB-SPEC §4.2) — без чисел, их скажет движок. */
const FB_WAIT_REASON_TEXT_RU = {
    post_limit: 'под этим роликом за час уже много наших действий',
};
/** Почему ждёт — словами; `network` — имя сети в фразе. */
export function describeWaitReason(reason, network) {
    if (reason === null)
        return 'очередь';
    const fb = network === 'facebook' ? FB_WAIT_REASON_TEXT_RU[reason] : undefined;
    if (fb !== undefined)
        return fb;
    const known = WAIT_REASON_TEXT_RU[reason];
    if (known !== undefined)
        return netText(known, network);
    return isPhraseRu(reason) ? reason : 'очередь';
}
//# sourceMappingURL=outcome-text.js.map